import React, { useEffect, useState } from "react";
import api from "../api";
import { Spinner } from "../components/ui-bits";
import { ActionMenu, ConfirmDialog } from "../components/crud";
import { TaskForm } from "../components/record-forms";
import { useToast } from "../hooks/use-toast";
import { Bot, Plus, Sparkles, PenLine, FlaskConical, Users, Play, Check, Clock, Loader2 } from "lucide-react";

const columns = [
  { key: "queued", label: "Queued", dot: "bg-[hsl(240,5%,70%)]" },
  { key: "in_progress", label: "In progress", dot: "bg-blue-500" },
  { key: "done", label: "Done", dot: "bg-emerald-500" },
];

const typeIcon = { sequence: PenLine, research: FlaskConical, content: Sparkles, enrichment: Users, general: Bot };

const nextStatus = { queued: "in_progress", in_progress: "done", done: "queued" };
const nextLabel = { queued: "Start", in_progress: "Complete", done: "Reopen" };
const nextIcon = { queued: Play, in_progress: Check, done: Clock };

export default function TasksPage() {
  const { toast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => { api.tasks().then(setTasks).finally(() => setLoading(false)); }, []);

  const advance = async (t) => {
    setBusy(t.id);
    try {
      const updated = await api.updateTask(t.id, { status: nextStatus[t.status] });
      setTasks((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
      toast({ title: `Task ${nextStatus[t.status].replace("_", " ")}` });
    } finally { setBusy(null); }
  };

  const submit = async (values) => {
    const task = modal.mode === "create" ? await api.createTask(values) : await api.updateTask(modal.row.id, values);
    setTasks((prev) => modal.mode === "create" ? [task, ...prev] : prev.map((item) => item.id === task.id ? task : item));
    setModal(null);
    toast({ title: `Task ${modal.mode === "create" ? "created" : "updated"}`, description: task.title });
  };

  const remove = async () => {
    setDeleteBusy(true);
    try {
      await api.deleteTask(deleting.id);
      setTasks((prev) => prev.filter((item) => item.id !== deleting.id));
      toast({ title: "Task deleted", description: deleting.title });
      setDeleting(null);
    } catch (error) {
      toast({ title: "Could not delete task", description: error?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="h-full flex flex-col animate-in-up">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[hsl(240,6%,92%)]">
        <div className="flex items-center gap-1.5 text-[13px] text-[hsl(240,4%,45%)]"><Bot className="h-4 w-4" /> Everything Hermes is working on for you</div>
        <button onClick={() => setModal({ mode: "create", row: {} })} className="ml-auto inline-flex items-center gap-1.5 rounded-md brand-bg text-white px-2.5 py-[6px] text-[13px] font-medium hover:brightness-105"><Plus className="h-4 w-4" /> New task</button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1100px] mx-auto">
          {columns.map((col) => {
            const items = tasks.filter((t) => t.status === col.key);
            return (
              <div key={col.key} className="rounded-xl border border-[hsl(240,6%,91%)] bg-[hsl(240,20%,99%)]">
                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[hsl(240,6%,92%)]">
                  <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                  <span className="text-[13px] font-semibold">{col.label}</span>
                  <span className="text-[12px] text-[hsl(240,4%,55%)]">{items.length}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {items.length === 0 && <div className="text-[12.5px] text-[hsl(240,4%,58%)] px-2 py-4 text-center">Nothing here.</div>}
                  {items.map((t) => {
                    const Icon = typeIcon[t.type] || Bot;
                    const NextIcon = nextIcon[t.status];
                    return (
                      <div key={t.id} className="rounded-lg border border-[hsl(240,6%,91%)] bg-white p-3">
                        <div className="flex items-start gap-2">
                          <div className="h-7 w-7 rounded-lg brand-bg-soft flex items-center justify-center shrink-0"><Icon className="h-3.5 w-3.5 text-[hsl(245,50%,48%)]" /></div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-medium leading-snug">{t.title}</div>
                            <div className="text-[11.5px] text-[hsl(240,4%,55%)] mt-0.5">{t.related_to} · {t.agent}</div>
                          </div>
                          <ActionMenu onEdit={() => setModal({ mode: "edit", row: t })} onDelete={() => setDeleting(t)} />
                        </div>
                        <button onClick={() => advance(t)} disabled={busy === t.id} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-[hsl(240,6%,88%)] px-2 py-1 text-[12px] font-medium hover:bg-[hsl(240,5%,96%)]">
                          {busy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <NextIcon className="h-3.5 w-3.5" />} {nextLabel[t.status]}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <TaskForm open={!!modal} onClose={() => setModal(null)} onSubmit={submit} initialValues={modal?.row} />
      <ConfirmDialog open={!!deleting} onClose={() => !deleteBusy && setDeleting(null)} onConfirm={remove} busy={deleteBusy} title="Delete task?" description={`“${deleting?.title || "This task"}” will be permanently removed.`} />
    </div>
  );
}
